import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Collapse, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { TChangePasswordFormData } from 'src/@types';
import { useStores } from 'src/models';
import { INIT_CHANGE_PASSWORD_DATA } from 'src/static/constants';
import changePwdFormValidationSchema from 'src/static/yup/changePwdFormValidationSchema';

import { RHFTextField } from '../hook-form';
import FormProvider from '../hook-form/FormProvider';

// 비밀번호 변경 컴포넌트
const ChangePassword = observer(() => {
  const { authStore } = useStores();
  const { enqueueSnackbar } = useSnackbar();
  const resolver = yupResolver(changePwdFormValidationSchema);
  const methods = useForm<TChangePasswordFormData>({
    defaultValues: INIT_CHANGE_PASSWORD_DATA,
    resolver,
  });

  const [hidePassword] = useState(true);

  const onSubmit = async (data: TChangePasswordFormData) => {
    const result = await authStore.changePassword(data);
    if (result) {
      // 비밀번호 변경 성공 시
      if (result.data.success) {
        enqueueSnackbar('비밀번호가 변경되었습니다.', { variant: 'success' });
        return;
      }

      // 비밀번호 변경 실패 시
      enqueueSnackbar(result.data.message, { variant: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <Collapse in={hidePassword} unmountOnExit>
        <StyledInnerContainer sx={{ width: '100%' }}>
          <StyledLabel>비밀번호 변경</StyledLabel>
          <StyledFormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
            <RHFTextField
              size="small"
              name="currentPwd"
              label="현재 비밀번호"
              type="password"
              fullWidth
            />
            <RHFTextField
              size="small"
              name="pwd"
              label="변경할 비밀번호"
              type="password"
              fullWidth
            />
            <RHFTextField
              size="small"
              name="pwdCorrect"
              label="변경할 비밀번호 확인"
              type="password"
              fullWidth
            />
            <SubmitButton variant="contained" type="submit" size="small">
              변경
            </SubmitButton>
          </StyledFormProvider>
        </StyledInnerContainer>
      </Collapse>
    </Box>
  );
});

export default ChangePassword;

const StyledInnerContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: `${theme.spacing(4)} ${theme.spacing(2)}`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledFormProvider = styled(FormProvider)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SubmitButton = styled(Button)(() => ({
  alignSelf: 'flex-end',
}));

const StyledLabel = styled('span')(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 700,
  padding: theme.spacing(0, 1),
  color: theme.palette.text.secondary,
  position: 'absolute',
  top: '-12px',
  left: '20px',
  background: theme.palette.background.paper,
}));
